import ChevronLeftThinIcon from '@assets/icons/chevron-left-thin.svg';
import ChevronRightThinIcon from '@assets/icons/chevron-right-thin.svg';
import moment, { Moment } from 'moment';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { Text, TouchableOpacity, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useHover } from 'react-native-web-hooks';

const MONTH_NAMES = moment.months();
const DAY_NAMES = moment.weekdaysShort(true);

interface Props {
  activeDate?: Moment | null;
  isDateDisabled: (date: Moment) => boolean;
  onDateChange: (activeDate: Moment) => void;
  onDisabledDatePress?: () => void;
}

const MONTH_HITSLOP = {
  bottom: 20,
  left: 24,
  right: 24,
  top: 20,
};

const DAY_HITSLOP = {
  bottom: 10,
  left: 10,
  right: 10,
  top: 10,
};

type Week = Moment[];

type MomentMatrix = Week[];

interface DateCellProps {
  colIndex: number;
  date: Moment;
  isDateActive: boolean | null | undefined;
  isDateDisabled: boolean;
  isDateTodayAndNotActive: boolean;
  isSameMonth: boolean;
  onDateChange: (date: Moment) => void;
  onDisabledDatePress?: () => void;
  rowIndex: number;
}

const DateCell: React.FC<DateCellProps> = ({
  colIndex,
  date,
  isDateActive,
  isDateDisabled,
  isDateTodayAndNotActive,
  isSameMonth,
  onDateChange,
  onDisabledDatePress,
  rowIndex,
}) => {
  const [styles] = useStyles();
  const dateRef = useRef<React.ComponentRef<typeof TouchableOpacity>>(null);
  const isHovered = useHover(dateRef);

  const shouldShowHover =
    isHovered && !isDateActive && !isDateDisabled && isSameMonth;

  const dayLabel = `${date.month() + 1}/${date.date()}`;
  const dayTestID = isDateActive
    ? `active-day-${dayLabel}`
    : isDateDisabled
      ? `disabled-day-${dayLabel}`
      : `day-${dayLabel}`;

  return (
    <TouchableOpacity
      hitSlop={DAY_HITSLOP}
      key={`${rowIndex}x${colIndex}`}
      onPress={() => {
        if (isDateDisabled) {
          return onDisabledDatePress?.();
        }

        if (isSameMonth) {
          onDateChange(date);
        }
      }}
      ref={dateRef}
      style={[
        styles.baseColumn,
        isDateActive ? styles.dateActive : null,
        isDateTodayAndNotActive ? styles.dateToday : null,
        shouldShowHover ? styles.dateHover : null,
      ]}
    >
      <Text
        style={[
          TYPOGRAPHY.p,
          isDateDisabled ? styles.dateTextDisabled : null,
          isDateActive ? styles.dateTextActive : null,
        ]}
        testID={dayTestID}
      >
        {isSameMonth ? date.date() : ''}
      </Text>
    </TouchableOpacity>
  );
};

export const Calendar: React.FC<Props> = ({
  activeDate,
  isDateDisabled = () => false,
  onDateChange,
  onDisabledDatePress,
}) => {
  const [styles, theme] = useStyles();

  const currentMonth = moment().startOf('month');

  const [visibleMonth, setVisibleMonth] = useState(
    activeDate?.clone().startOf('month') || currentMonth.clone(),
  );

  const changeMonth = useCallback((changeValue: number) => {
    setVisibleMonth((previousVisibleMonth) =>
      previousVisibleMonth.clone().add(changeValue, 'month'),
    );
  }, []);

  const isSameMonth = useCallback(
    (date: Moment) => date.isSame(visibleMonth, 'month'),
    [visibleMonth],
  );

  const monthMatrix = useMemo(() => {
    const matrix: MomentMatrix = [];
    const startDay = visibleMonth.clone().startOf('week');
    const endDay = visibleMonth.clone().endOf('month').endOf('week');

    let date = startDay.clone().subtract(1, 'day');
    while (date.isBefore(endDay, 'day')) {
      matrix.push(
        Array(7)
          .fill(0)
          .map(() => date.add(1, 'day').clone()),
      );
    }

    return matrix;
  }, [visibleMonth]);

  const dates = useMemo(() => {
    const now = moment();
    return monthMatrix.map((row, rowIndex) => {
      return (
        <View key={rowIndex} style={styles.baseRow}>
          {row.map((date, colIndex) => {
            const isDateSameAsActive = activeDate && date.isSame(activeDate);
            const isDateActive = isDateSameAsActive && isSameMonth(date);
            const isDateTodayAndNotActive =
              !isDateSameAsActive && date.isSame(now, 'day');

            return (
              <DateCell
                colIndex={colIndex}
                date={date}
                isDateActive={isDateActive}
                isDateDisabled={isDateDisabled(date)}
                isDateTodayAndNotActive={isDateTodayAndNotActive}
                isSameMonth={isSameMonth(date)}
                key={`${rowIndex}x${colIndex}`}
                onDateChange={onDateChange}
                onDisabledDatePress={onDisabledDatePress}
                rowIndex={rowIndex}
              />
            );
          })}
        </View>
      );
    });
  }, [
    activeDate,
    isDateDisabled,
    isSameMonth,
    monthMatrix,
    onDateChange,
    onDisabledDatePress,
    styles,
  ]);

  const isCurrentMonthVisible = isSameMonth(currentMonth);
  return (
    <View>
      <View style={styles.calendarHeader}>
        <Typography color={theme.sk.color.text.primary} variant="heading-md">
          {MONTH_NAMES[visibleMonth.month()]} {visibleMonth.year()}
        </Typography>
        <View style={styles.pageArrowRow}>
          <TouchableOpacity
            disabled={isCurrentMonthVisible}
            hitSlop={MONTH_HITSLOP}
            onPress={() => changeMonth(-1)}
            style={styles.arrowButton}
            testID="calendar-left-arrow"
          >
            <ChevronLeftThinIcon
              height={24}
              stroke={
                isCurrentMonthVisible
                  ? theme.sk.color.icon.disabled
                  : theme.sk.color.icon.primary
              }
              width={24}
            />
          </TouchableOpacity>
          <TouchableOpacity
            hitSlop={MONTH_HITSLOP}
            onPress={() => changeMonth(1)}
            style={styles.arrowButton}
            testID="calendar-right-arrow"
          >
            <ChevronRightThinIcon
              height={24}
              stroke={theme.sk.color.icon.primary}
              width={24}
            />
          </TouchableOpacity>
        </View>
      </View>
      <View style={[styles.baseRow, styles.weekdayRow]}>
        {DAY_NAMES.map((dayName, index) => (
          <View key={`${dayName}-${index}`} style={styles.weekdayColumn}>
            <Typography color={theme.sk.color.text.hint} variant="label-md">
              {dayName}
            </Typography>
          </View>
        ))}
      </View>
      {dates}
    </View>
  );
};

const DATE_CELL_SIZE = 44;
const DATE_CELL_BORDER_RADIUS = DATE_CELL_SIZE / 2;

const useStyles = createStyles((theme) => ({
  arrowButton: {
    alignItems: 'center',
    height: theme.sk.spacing.spaceLarge,
    justifyContent: 'center',
    width: theme.sk.spacing.spaceLarge,
  },
  baseColumn: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: DATE_CELL_BORDER_RADIUS,
    height: DATE_CELL_SIZE,
    justifyContent: 'center',
    width: DATE_CELL_SIZE,
  },
  baseRow: {
    backgroundColor: theme.sk.color.bg.primary.default,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
  calendarHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  dateActive: {
    backgroundColor: theme.sk.color.bg.dark.default,
  },
  dateHover: {
    borderColor: theme.sk.color.border.button.secondary.default,
    borderRadius: DATE_CELL_BORDER_RADIUS,
    borderWidth: 1.5,
  },
  dateTextActive: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.ondark,
  },
  dateTextDisabled: {
    opacity: 0.4,
  },
  dateToday: {
    borderColor: theme.colors.inactiveFill,
    borderRadius: DATE_CELL_BORDER_RADIUS,
    borderWidth: 1,
  },
  pageArrowRow: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    marginRight: theme.sk.spacing.spaceSmall,
  },
  weekdayColumn: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    height: theme.sk.spacing.spaceLarge,
    justifyContent: 'center',
    width: theme.sk.spacing.space2xlarge,
  },
  weekdayRow: {
    backgroundColor: theme.sk.color.bg.primary.default,
    flexDirection: 'row',
    height: theme.sk.spacing.spaceLarge,
    justifyContent: 'space-evenly',
    marginBottom: theme.sk.spacing.spaceXsmall,
    paddingVertical: 0,
  },
}));
